import React, { useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { EventHandler } from '@tinymce/tinymce-react/lib/cjs/main/ts/Events';
import { Editor, EditorEvent, Events } from 'tinymce';

import { containsHTMLTags, nl2p } from 'helpers/utils';
import Icon from 'ui/icon/Icon';

import BundledEditor from './BundledEditor';
import getAutocompleterContext from './getAutocompleterContext';
import { postProcess, preProcess } from './utils';

const MIN_HEIGHT = 200;

type CustomAction = {
  key: string;
  onClick: () => void;
  icon: UIIconName;
  label: string;
};

export type AutocompleterItem = {
  value: string;
  text: string;
  meta?: Record<string, unknown>;
};

export type AutocompleterConfig = {
  id: string;
  trigger: string;
  minChars?: number;
  fetch: (pattern: string) => Promise<AutocompleterItem[]> | AutocompleterItem[];
  onAction: (item: AutocompleterItem, editor: any) => void;
};

type Props = {
  id?: string;
  initialValue?: string;
  autofocus?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  onKeyDown?: EventHandler<KeyboardEvent>;
  onInit?: EventHandler<any>;
  customActions?: CustomAction[];
  autocompleters?: AutocompleterConfig[];
  onLoad?: () => void;
  onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLDivElement>) => void;
};

function RichTextEditor({
  id,
  initialValue,
  autofocus,
  placeholder,
  onChange,
  onKeyDown,
  onInit,
  customActions,
  autocompleters,
  onLoad,
  onDrop,
  onPaste,
}: Props) {
  const [initialValueInner, setInitialValueInner] = useState(initialValue);

  const editorRef = useRef<any>(null);
  const isReadyRef = useRef(false);

  const toolbarCustomButtons = () => {
    const items = customActions?.map((action) => action.key) ?? [];

    if (items.length === 0) return '';

    return `${items.join(' ')} | `;
  };

  const getInitialValue = () => {
    if (!initialValueInner) return '';
    return containsHTMLTags(initialValueInner) ? preProcess(initialValueInner) : nl2p(initialValueInner);
  };

  useEffect(() => {
    if (initialValueInner === undefined || initialValueInner === null) {
      setInitialValueInner(initialValue);
    }
  }, [initialValue]);

  return (
    <BundledEditor
      id={id}
      initialValue={getInitialValue()}
      onSetContent={(e) => {
        if (!isReadyRef.current) return;
        onChange(postProcess(e.target.getContent()));
      }}
      onChange={(e) => {
        onChange(postProcess(e.target.getContent()));
      }}
      onInput={(_e, editor) => {
        onChange(postProcess(editor.getContent()));
      }}
      onKeyDown={(event: EditorEvent<KeyboardEvent>, editor: Editor) => {
        if (event.key === 'Escape' && event.defaultPrevented) return;
        onKeyDown?.(event, editor);
      }}
      onInit={onInit}
      onDrop={onDrop}
      onPaste={(event, editor) => {
        if (!event.clipboardData) return;

        if (event.clipboardData?.getData('text/html')) return;

        // If the clipboard data contains plain text, keep the number of newlines
        const text = event.clipboardData?.getData('text/plain');
        if (text && text.length > 0 && !containsHTMLTags(text)) {
          editor.execCommand(
            'mceInsertContent',
            false,
            text.replace(
              /\n{2,}/g,
              (match) =>
                `\n${Array(match.length - 1)
                  .fill('<p></p>')
                  .join('')}`,
            ),
          );
          // mceInsertContent does not fire input, so autocomplete needs an explicit refresh after paste.
          editor.execCommand('mceAutocompleterReload');
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
        }
        onPaste?.(event);
      }}
      init={{
        // called after the react wrapper's own init setContent, so content
        // normalization during mount never reaches onChange
        init_instance_callback: () => {
          isReadyRef.current = true;
        },
        min_height: MIN_HEIGHT,
        autoresize_bottom_margin: 0,
        menubar: false,
        auto_focus: autofocus ? true : undefined,
        placeholder,
        plugins: ['autoresize', 'autolink', 'lists', 'link'],
        toolbar: `bold italic forecolor |
          alignleft aligncenter alignright alignjustify |
          link | bullist numlist outdent indent | removeformat |
          ${toolbarCustomButtons()}`,
        setup: (editor) => {
          editorRef.current = editor;
          // autoresize plugin misses late reflows (webfont load) and caches its
          // last size, so mceAutoResize is a no-op; set the height directly
          editor.on('init', () => {
            const doc = editor.getDoc();
            if (!doc) return;
            const observer = new ResizeObserver(() => {
              const container = editor.getContainer();
              const contentArea = editor.getContentAreaContainer();
              if (!container || !contentArea) return;
              const chromeHeight = container.offsetHeight - contentArea.offsetHeight;
              const total = Math.max(doc.documentElement.offsetHeight + chromeHeight, MIN_HEIGHT);
              if (Math.abs(container.offsetHeight - total) > 1) {
                container.style.height = `${total}px`;
              }
            });
            observer.observe(doc.documentElement);
            editor.on('remove', () => observer.disconnect());
          });
          customActions?.forEach((action) => {
            editor.ui.registry.addIcon(
              action.icon,
              ReactDOMServer.renderToString(<Icon name={action.icon} className="h-5 w-5 fill-none" />),
            );
            editor.ui.registry.addButton(action.key, {
              icon: action.icon,
              tooltip: action.label,
              onAction: () => action.onClick(),
            });
          });

          autocompleters?.forEach((ac) => {
            let pattern = '';
            editor.ui.registry.addAutocompleter(ac.id, {
              trigger: ac.trigger,
              minChars: 0,
              columns: 1,
              matches: (range: Range) => {
                const context = getAutocompleterContext(editor, range, ac.trigger);
                if (!context) return false;
                range.setStart(context.range.startContainer, context.range.startOffset);
                pattern = context.pattern;
                return pattern.length >= (ac.minChars ?? 0);
              },
              fetch: async () => {
                const items = await ac.fetch(pattern);
                return items.map((item) => ({ value: item.value, text: item.text, meta: item.meta }));
              },
              onAction: (autocompleteApi: any, rng: Range, value: string, meta: Record<string, unknown>) => {
                // TinyMCE refreshes the active range from the nearest trigger before onAction.
                const context = getAutocompleterContext(editor, rng, ac.trigger);
                if (!context) {
                  autocompleteApi.hide();
                  return;
                }
                editor.selection.setRng(context.range);
                ac.onAction({ value, text: '', meta }, editor);
                autocompleteApi.hide();
              },
            });
          });

          let isAutocompleterOpen = false;
          editor.on(
            'AutocompleterStart AutocompleterUpdate',
            ({ lookupData }: Events.EditorEventMap['AutocompleterStart']) => {
              isAutocompleterOpen = lookupData.some((lookup) => lookup.items.length > 0);
            },
          );
          editor.on('AutocompleterEnd', () => {
            isAutocompleterOpen = false;
          });
          editor.on('init', () => {
            const doc = editor.getDoc();
            const handleKeyDown = (event: KeyboardEvent) => {
              // TinyMCE closes autocomplete before the consumer's keydown handler runs.
              if (event.key === 'Escape' && isAutocompleterOpen) event.preventDefault();
            };
            doc.addEventListener('keydown', handleKeyDown, true);
            editor.on('remove', () => doc.removeEventListener('keydown', handleKeyDown, true));
          });

          if (placeholder) {
            // TinyMCE does not hide the placeholder after AltGr input (e.g. @ on a Czech keyboard).
            editor.on('input', () => {
              const body = editor.getBody();
              if (!editor.dom.isEmpty(body)) {
                editor.dom.setAttrib(body, 'data-mce-placeholder', null);
              }
            });
          }

          if (onLoad) {
            const computePaddingBottom = () => {
              const body = editor.getBody();
              const cs = getComputedStyle(body);
              if (editor.container && body.offsetHeight > 0) {
                body.style.paddingBottom = `${Math.max(
                  MIN_HEIGHT -
                    (editor.container.getElementsByClassName('tox-editor-header')[0] as HTMLElement).offsetHeight -
                    (body.offsetHeight - parseFloat(cs.paddingBottom)),
                  0,
                )}px`;
              }
            };
            const firstResizeHandler = () => {
              onLoad();
              computePaddingBottom();
              editor.off('ResizeEditor', firstResizeHandler);
            };

            editor.on('ResizeEditor', firstResizeHandler);

            editor.on('input', () => {
              computePaddingBottom();
            });
            editor.on('setcontent', () => {
              computePaddingBottom();
            });
          }
        },
        content_css: `${window.appState.baseStaticUrl}tinymce/mentions.css`,
        content_style: `
            body::before { cursor: text; }
            body {
              font-family: "Inter var",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
              margin: 1rem;
              line-height: 1.4;
            }
            ul,ol { list-style-position: inside; padding-inline-start: 0 }
            ul ul { list-style-type: disc; }
            ul ul,ol ol { padding-left: 1rem; }
            a { color: #4f46e5; text-decoration: none; }
            a:hover { color: #3730a3; }
            p { margin: 0; }`,
        branding: false,
        statusbar: false,
        font_css: `${window.appState.baseStaticUrl}fonts/inter/inter.css`,
        indentation: '32px',
        contextmenu: '',
        browser_spellcheck: true,
      }}
    />
  );
}

export default RichTextEditor;
